import React, { useState } from "react";
import { authService } from "../../services/auth.service";
import GoogleIcon from "../../assets/icons/GoogleIcon";
import AppleIcon from "../../assets/icons/AppleIcon";
import "./AuthPage.css";

const RegisterPage = ({ onToggleView }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(""); // 'weak', 'medium', 'strong'
  const [globalError, setGlobalError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  // --- REGEX PATTERNS ---
  const PATTERNS = {
    email: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/,
    // Strict: 8 chars, 1 upper, 1 lower, 1 number, 1 special
    strongPassword: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/,
    // Indian Phone: Starts with 6-9, followed by 9 digits (total 10)
    phone: /^[6-9]\d{9}$/,
  };

  // --- PASSWORD STRENGTH LOGIC ---
  const checkPasswordStrength = (pass) => {
    if (!pass) return "";
    if (pass.length < 8) return "weak"; // Too short

    const hasUpper = /[A-Z]/.test(pass);
    const hasLower = /[a-z]/.test(pass);
    const hasNum = /\d/.test(pass);
    const hasSpec = /[!@#$%^&*(),.?":{}|<>]/.test(pass);

    // Count how many complexity criteria are met
    const criteriaCount = [hasUpper, hasLower, hasNum, hasSpec].filter(Boolean).length;

    // Must have length 8+ AND all 4 criteria to be "Strong" (Green)
    if (pass.length >= 8 && criteriaCount === 4) return "strong";
    
    // Otherwise it's Medium (Yellow)
    return "medium";
  };

  // --- VALIDATION LOGIC ---
  const validateField = (name, value) => {
    let errorMsg = "";

    switch (name) {
      case "firstName":
      case "lastName":
        if (!value.trim()) errorMsg = "Required.";
        break;

      case "phone":
        if (!value) errorMsg = "Phone number is required.";
        else if (!PATTERNS.phone.test(value)) {
          errorMsg = "Must be a valid 10-digit Indian number (starts with 6-9).";
        }
        break;

      case "email":
        if (!value) errorMsg = "Email is required.";
        else if (!PATTERNS.email.test(value)) {
          errorMsg = "Invalid email address.";
        }
        break;

      case "password":
        if (!value) errorMsg = "Password is required.";
        else {
          const strength = checkPasswordStrength(value);
          if (strength !== "strong") {
             // We allow typing, but we set error if they leave field and it's not strong
             errorMsg = "Password is not strong enough.";
          }
        }
        break;

      case "confirmPassword":
        if (!value) errorMsg = "Confirm Password is required.";
        else if (value !== formData.password) {
          errorMsg = "Passwords do not match.";
        }
        break;

      default:
        break;
    }
    return errorMsg;
  };

  const handleChange = (e) => {
    const { id, value } = e.target;

    // Special handling for PHONE: restrict to numbers and max 10 digits
    if (id === "phone") {
      // Allow only digits
      const onlyNums = value.replace(/\D/g, "");
      // Slice to max 10
      const truncated = onlyNums.slice(0, 10);
      
      setFormData({ ...formData, [id]: truncated });
      if (errors[id]) setErrors({ ...errors, [id]: "" });
      return;
    }

    // Special handling for PASSWORD: Check strength live
    if (id === "password") {
      setPasswordStrength(checkPasswordStrength(value));
    }

    setFormData({ ...formData, [id]: value });
    
    // Clear error immediately when user types
    if (errors[id]) {
      setErrors({ ...errors, [id]: "" });
    }
  };

  const handleBlur = (e) => {
    const { id, value } = e.target;
    const error = validateField(id, value);
    setErrors((prev) => ({ ...prev, [id]: error }));
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setGlobalError(null);
    setSuccessMessage(null);

    // Check all fields
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    // Ensure password is strictly STRONG (Green)
    if (passwordStrength !== 'strong') {
        newErrors.password = "Password must be Strong (Green) to register.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      await authService.register(
        formData.firstName,
        formData.lastName,
        formData.phone,
        formData.email,
        formData.password
      );
      setSuccessMessage("Account created successfully! Redirecting...");
      setTimeout(() => {
        // App.js usually handles redirect via auth state listener
      }, 1500);
    } catch (err) {
      console.error("Registration failed:", err);
      let msg = "Registration failed.";
      if (err.code === 'auth/email-already-in-use') msg = "Email already in use.";
      setGlobalError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    // Google logic...
  };

  return (
    <div className="auth-card">
      <div className="auth-card-header">
        <h2>Create Account</h2>
        <p>Sign up to get started</p>
        
        {globalError && <div className="message-banner error">{globalError}</div>}
        {successMessage && <div className="message-banner success">{successMessage}</div>}
      </div>

      <form className="auth-form" onSubmit={handleSignup} noValidate>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="firstName">First Name</label>
            <input
              type="text"
              id="firstName"
              value={formData.firstName}
              onChange={handleChange}
              onBlur={handleBlur}
              className={errors.firstName ? "input-error" : ""}
            />
            {errors.firstName && <span className="error-text">{errors.firstName}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="lastName">Last Name</label>
            <input
              type="text"
              id="lastName"
              value={formData.lastName}
              onChange={handleChange}
              onBlur={handleBlur}
              className={errors.lastName ? "input-error" : ""}
            />
            {errors.lastName && <span className="error-text">{errors.lastName}</span>}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="phone">Phone Number</label>
          <input
            type="tel"
            id="phone"
            value={formData.phone}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="9876543210"
            className={errors.phone ? "input-error" : ""}
          />
          {errors.phone && <span className="error-text">{errors.phone}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="email">Email Address</label>
          <input
            type="email"
            id="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            className={errors.email ? "input-error" : ""}
          />
          {errors.email && <span className="error-text">{errors.email}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="password">Password</label>
          <input
            type="password"
            id="password"
            value={formData.password}
            onChange={handleChange}
            onBlur={handleBlur}
            className={errors.password ? "input-error" : ""}
          />
          
          {/* PASSWORD STRENGTH BAR */}
          {formData.password && (
            <div className="password-strength-container">
              <div className={`strength-bar ${passwordStrength}`}></div>
              <span className={`strength-text ${passwordStrength}`}>
                {passwordStrength === 'weak' && "Weak"}
                {passwordStrength === 'medium' && "Medium"}
                {passwordStrength === 'strong' && "Strong"}
              </span>
            </div>
          )}

          {errors.password && <span className="error-text">{errors.password}</span>}
          
          <small className="form-hint">
             Must conform to 'Strong' criteria (8+ chars, Upper, Lower, Num, Special)
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm Password</label>
          <input
            type="password"
            id="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            onBlur={handleBlur}
            className={errors.confirmPassword ? "input-error" : ""}
          />
          {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
        </div>

        <button type="submit" className="auth-btn" disabled={loading}>
          {loading ? "Creating Account..." : "Sign Up"}
        </button>
      </form>

      <div className="auth-divider"><span>OR</span></div>
      
      <button className="google-btn" onClick={handleGoogleSignup} type="button">
        <GoogleIcon /> Sign Up with Google
      </button>
      <button className="google-btn" style={{ marginTop: "10px" }} type="button">
        <AppleIcon /> Sign Up with Apple
      </button>

      <div className="auth-toggle">
        Already have an account? <button onClick={onToggleView}>Sign In</button>
      </div>
    </div>
  );
};

export default RegisterPage;